import { InfoOutlined, CheckCircleOutline, WarningAmber, FileDownload } from '@mui/icons-material';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import typhiCipNSRaw from '../../../../assets/typhiCipNS.json';
import ecoliESBLRaw from '../../../../assets/ecoliESBL.json';
import kpneumoCarbapenemRaw from '../../../../assets/kpneumoCarbapenem.json';
import saureusMRSARaw from '../../../../assets/saureusMRSA.json';
import salmonellaCiprofloxacinRaw from '../../../../assets/salmonellaCiprofloxacin.json';
import decoliCiprofloxacinRaw from '../../../../assets/decoliCiprofloxacin.json';
import shigeCiprofloxacinRaw from '../../../../assets/shigeCiprofloxacin.json';
import ngonoCiprofloxacinRaw from '../../../../assets/ngonoCiprofloxacin.json';
import strepneumoCoTrimoxazoleRaw from '../../../../assets/strepneumoCoTrimoxazole.json';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  ErrorBar,
  Label,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
  ZAxis,
  Cell,
} from 'recharts';
import { useAppSelector } from '../../../../stores/hooks';
import {
  fetchGLASSData,
  getGLASSIndicatorForATBClass,
  getGLASSIndicatorForOrganism,
  getGLASSPhenotypicByOrganismDrug,
} from '../../../../data/glass_data';
import { atbToAmrnetMapping, getAmrnetDrugsForATBClass } from '../../../../util/atbDrugMapping';
import { PlottingOptionsHeader } from '../../Shared/PlottingOptionsHeader';
import { PlottingOptionsPanel } from '../../Shared/PlottingOptionsPanel';
import { useStyles } from './GenomicVsPhenotypicGraphMUI';

// Public-facing source landing pages used by the Data Sources panel.
const SOURCE_URLS = {
  glass: 'https://www.who.int/data/gho/data/themes/topics/global-antimicrobial-resistance-and-use-surveillance-system-glass-database',
  whoTerms: 'https://www.who.int/about/policies/publishing/data-policy/terms-and-conditions',
  ecdc: 'https://www.ecdc.europa.eu/en/antimicrobial-resistance/surveillance-and-disease-data/data-ecdc',
  amrnet: 'https://amrnet.readthedocs.io',
};

// Data-governance hold: the bundled "literature" phenotypic AMR datasets are
// disabled in the dashboard until an agreed identification/aggregation protocol
// exists (how each value was found, breakpoint harmonisation, per-value
// provenance, and how a user can verify a number). Blindly combining phenotypic
// AMR from heterogeneous literature is not yet methodologically signed off.
// Flip to `true` to re-enable once that protocol is in place. The JSON files and
// all rendering code are kept intact so this is a one-line reversal.
const LITERATURE_ENABLED = false;

// Organisms that have bundled literature surveillance data
const LITERATURE_ORGANISMS = new Set(
  LITERATURE_ENABLED
    ? ['styphi', 'ecoli', 'kpneumo', 'saureus', 'senterica', 'sentericaints', 'decoli', 'shige', 'ngono', 'strepneumo']
    : [],
);

// Lookup map: organism → raw JSON (null = handled separately for styphi)
// senterica and sentericaints share the same Salmonella CIP dataset
const LITERATURE_RAW = {
  ecoli: ecoliESBLRaw,
  kpneumo: kpneumoCarbapenemRaw,
  saureus: saureusMRSARaw,
  senterica: salmonellaCiprofloxacinRaw,
  sentericaints: salmonellaCiprofloxacinRaw,
  decoli: decoliCiprofloxacinRaw,
  shige: shigeCiprofloxacinRaw,
  ngono: ngonoCiprofloxacinRaw,
  strepneumo: strepneumoCoTrimoxazoleRaw,
};

// Default phenoSource per organism
function defaultPhenoSource(organism) {
  if (!LITERATURE_ENABLED) return 'glass';
  if (organism === 'styphi') return 'typhi_literature';
  if (LITERATURE_ORGANISMS.has(organism)) return 'literature';
  return 'glass';
}

// ISO3 → AMRnet country name for Typhi literature data matching
const TYPHI_ISO3_TO_AMRNET = {
  BGD: 'Bangladesh', NPL: 'Nepal', PAK: 'Pakistan', IND: 'India',
  KHM: 'Cambodia', VNM: 'Vietnam', LAO: 'Laos', IDN: 'Indonesia',
  PHL: 'Philippines', KEN: 'Kenya', TZA: 'Tanzania', ZWE: 'Zimbabwe',
  MWI: 'Malawi', ZMB: 'Zambia', ZAF: 'South Africa', GHA: 'Ghana',
  NGA: 'Nigeria', UGA: 'Uganda',
};

const CONCORDANCE_COLORS = {
  concordant: '#2e7d32',
  overestimate: '#e65100',
  underestimate: '#1565c0',
};


/**
 * Wilson score 95% confidence interval for a proportion.
 * More appropriate than normal approximation for small samples or extreme proportions.
 */
function wilsonCI(successes, total) {
  if (total === 0) return { lower: 0, upper: 0 };
  const p = successes / total;
  const z = 1.96; // 95% CI
  const z2 = z * z;
  const denom = 1 + z2 / total;
  const center = (p + z2 / (2 * total)) / denom;
  const margin = (z / denom) * Math.sqrt((p * (1 - p)) / total + z2 / (4 * total * total));
  return {
    lower: Math.max(0, (center - margin) * 100),
    upper: Math.min(100, (center + margin) * 100),
  };
}

/**
 * Pearson correlation coefficient
 */
function pearsonR(data) {
  const n = data.length;
  if (n < 3) return { r: 0, r2: 0, p: 1 };
  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0, sumY2 = 0;
  data.forEach(d => { sumX += d.x; sumY += d.y; sumXY += d.x * d.y; sumX2 += d.x * d.x; sumY2 += d.y * d.y; });
  const num = n * sumXY - sumX * sumY;
  const den = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));
  const r = den > 0 ? num / den : 0;
  // Approximate p-value using t-distribution
  const t = r * Math.sqrt((n - 2) / (1 - r * r + 1e-10));
  // Simplified p-value approximation for |t| with n-2 df
  const df = n - 2;
  const p = df > 0 ? Math.exp(-0.717 * Math.abs(t) - 0.416 * t * t / df) : 1; // rough approximation
  return { r, r2: r * r, p: Math.min(1, p) };
}

/**
 * Spearman rank correlation
 */
function spearmanRho(data) {
  const n = data.length;
  if (n < 3) return { rho: 0, p: 1 };

  const rankArray = (arr) => {
    const sorted = [...arr].map((v, i) => ({ v, i })).sort((a, b) => a.v - b.v);
    const ranks = new Array(arr.length);
    let i = 0;
    while (i < sorted.length) {
      let j = i;
      while (j < sorted.length - 1 && sorted[j + 1].v === sorted[j].v) j++;
      const avgRank = (i + j) / 2 + 1;
      for (let k = i; k <= j; k++) ranks[sorted[k].i] = avgRank;
      i = j + 1;
    }
    return ranks;
  };

  const xRanks = rankArray(data.map(d => d.x));
  const yRanks = rankArray(data.map(d => d.y));

  let sumD2 = 0;
  for (let i = 0; i < n; i++) sumD2 += Math.pow(xRanks[i] - yRanks[i], 2);
  const rho = 1 - (6 * sumD2) / (n * (n * n - 1));
  // Approximate p-value
  const t = rho * Math.sqrt((n - 2) / (1 - rho * rho + 1e-10));
  const p = Math.exp(-0.717 * Math.abs(t) - 0.416 * t * t / Math.max(n - 2, 1));
  return { rho, p: Math.min(1, p) };
}

/**
 * Linear regression with slope, intercept, R²
 */
function linearRegression(data) {
  const n = data.length;
  if (n < 2) return { slope: 0, intercept: 0, r2: 0 };
  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
  data.forEach(d => { sumX += d.x; sumY += d.y; sumXY += d.x * d.y; sumX2 += d.x * d.x; });
  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;
  const ssRes = data.reduce((s, d) => s + Math.pow(d.y - (slope * d.x + intercept), 2), 0);
  const ssTot = data.reduce((s, d) => s + Math.pow(d.y - sumY / n, 2), 0);
  return { slope, intercept, r2: ssTot > 0 ? 1 - ssRes / ssTot : 0 };
}

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  const diff = d.y - d.x;
  return (
    <Box sx={{ backgroundColor: '#fff', padding: '8px 12px', border: '1px solid rgba(0,0,0,0.2)', borderRadius: '4px', maxWidth: 300 }}>
      <Typography variant="body2" fontWeight={600}>{d.country}</Typography>
      <Typography variant="caption" display="block">
        Phenotypic ({d.phenoSourceLabel || 'GLASS'}): <strong>{d.x?.toFixed(1)}%</strong> ({d.phenoYearRange || d.phenoYear}, N={d.phenoTested})
      </Typography>
      <Typography variant="caption" display="block">
        Genomic (AMRnet): <strong>{d.y?.toFixed(1)}%</strong> (95% CI: {d.genomicCILower?.toFixed(1)}–{d.genomicCIUpper?.toFixed(1)}%, N={d.genomes})
      </Typography>
      <Typography variant="caption" display="block" sx={{ color: d.category === 'concordant' ? '#2e7d32' : '#d32f2f' }}>
        Difference: {diff > 0 ? '+' : ''}{diff.toFixed(1)} pp
        {d.category === 'concordant' ? ' (concordant — within 95% CI)' : diff > 0 ? ' (genomic overestimates)' : ' (genomic underestimates)'}
      </Typography>
      {d.yearOverlap && (
        <Typography variant="caption" display="block" color="textSecondary">
          Time period overlap: {d.yearOverlap}
        </Typography>
      )}
    </Box>
  );
};

export const GenomicVsPhenotypicGraph = ({ showFilter, setShowFilter, filterButtonRef }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [glassData, setGlassData] = useState(null);
  const [loading, setLoading] = useState(true);

  const organism = useAppSelector(state => state.dashboard.organism);
  const drugsCountriesData = useAppSelector(state => state.graph.drugsCountriesData);
  const rawOrganismData = useAppSelector(state => state.graph.rawOrganismData);
  const timeInitial = useAppSelector(state => state.dashboard.timeInitial);
  const timeFinal = useAppSelector(state => state.dashboard.timeFinal);
  const actualTimeInitial = useAppSelector(state => state.dashboard.actualTimeInitial);
  const actualTimeFinal = useAppSelector(state => state.dashboard.actualTimeFinal);
  const canGetData = useAppSelector(state => state.dashboard.canGetData);
  const loadingPDF = useAppSelector(state => state.dashboard.loadingPDF);

  // Default ON: only consider phenotypic rows whose year falls inside the
  // dashboard's current genomic year range. Without this it's possible to
  // unknowingly compare 15-year-pooled genomic %R against a single 2021
  // phenotype point. The toggle lives in the controls row.
  const [restrictByYear, setRestrictByYear] = useState(true);

  // The dashboard's applied year range is what the genomic side actually uses
  // (drugsCountriesData is aggregated against actualTimeInitial..actualTimeFinal).
  // Cast to integers up front so the per-record year comparisons aren't
  // string-vs-number.
  const genoYearStart = Number(actualTimeInitial ?? timeInitial) || 0;
  const genoYearEnd = Number(actualTimeFinal ?? timeFinal) || 9999;

  // Enumerate every (drug, phenotype-source) pair for which we have BOTH
  // an AMRnet genomic record (drugsCountriesData entry) AND a phenotypic
  // source (WHO GLASS indicator or bundled literature dataset). This drives
  // the explicit Drug selector below — previously the drug was hardcoded
  // per organism and there was no visible indicator of what was being
  // plotted.
  const availableDrugs = useMemo(() => {
    const list = [];

    // GLASS-backed entries: one per ATB class with an indicator + an AMRnet
    // drug mapping. For most organisms this yields multiple options
    // (kpneumo: Carbapenems, Fluoroquinolones, Cephalosporins, …).
    const atbClasses = Object.keys(atbToAmrnetMapping[organism] || {});
    atbClasses.forEach(cls => {
      const indicator = getGLASSIndicatorForATBClass(organism, cls);
      const amrnetDrugs = getAmrnetDrugsForATBClass(organism, cls);
      if (indicator && amrnetDrugs.length > 0) {
        list.push({
          id: `glass:${cls}`,
          label: `${amrnetDrugs[0]} — WHO GLASS (${cls})`,
          source: 'glass',
          atbClass: cls,
          amrnetDrug: amrnetDrugs[0],
          indicator,
        });
      }
    });

    // Literature-backed entry (currently 1 per organism — the bundled JSON).
    // Surfaced as a separate row so the user can compare GLASS pheno vs
    // literature pheno for the same AMRnet drug.
    if (LITERATURE_ORGANISMS.has(organism)) {
      const primaryIndicator = getGLASSIndicatorForOrganism(organism);
      if (primaryIndicator) {
        list.push({
          id: 'literature',
          label: `${primaryIndicator.drug} — Literature`,
          source: organism === 'styphi' ? 'typhi_literature' : 'literature',
          atbClass: null,
          amrnetDrug: primaryIndicator.drug,
          indicator: primaryIndicator,
        });
      }
    }

    return list;
  }, [organism]);

  // Default selection: literature for organisms that have it (matches
  // previous defaultPhenoSource behaviour), otherwise the first GLASS row.
  const [selectedDrugId, setSelectedDrugId] = useState(null);
  useEffect(() => {
    if (availableDrugs.length === 0) {
      setSelectedDrugId(null);
      return;
    }
    const defaultId = LITERATURE_ORGANISMS.has(organism)
      ? (availableDrugs.find(d => d.id === 'literature')?.id ?? availableDrugs[0].id)
      : availableDrugs[0].id;
    setSelectedDrugId(defaultId);
  }, [organism, availableDrugs]);

  const selectedDrug = useMemo(
    () => availableDrugs.find(d => d.id === selectedDrugId) ?? availableDrugs[0] ?? null,
    [availableDrugs, selectedDrugId],
  );

  // phenoSource / glassIndicator are now derived from the selected entry so
  // the rest of the component (which switches on phenoSource string values)
  // doesn't need to change.
  const phenoSource = selectedDrug?.source ?? defaultPhenoSource(organism);
  const glassIndicator = useMemo(
    () => selectedDrug?.indicator ?? getGLASSIndicatorForOrganism(organism),
    [selectedDrug, organism],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchGLASSData()
      .then(data => { if (!cancelled) setGlassData(data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Build comparison data with proper statistics
  const { scatterData, regression, stats, correlations, rawPhenoData } = useMemo(() => {
    const empty = { scatterData: [], regression: { slope: 0, intercept: 0, r2: 0 }, stats: null, correlations: null, rawPhenoData: [] };
    if (!glassIndicator || !drugsCountriesData || typeof drugsCountriesData !== 'object') {
      return empty;
    }
    // For literature sources, we don't need glassData
    const usingLiterature = phenoSource === 'typhi_literature' || phenoSource === 'literature';
    if (!usingLiterature && !glassData) return empty;

    // Get phenotypic data
    let phenoData = [];
    let phenoSourceLabel = 'GLASS';

    if (phenoSource === 'literature' && LITERATURE_ORGANISMS.has(organism) && organism !== 'styphi') {
      // Bundled surveillance literature data — use LITERATURE_RAW lookup
      const rawLit = LITERATURE_RAW[organism] || null;
      if (rawLit) {
        phenoData = rawLit.data
          .filter(e => e.resistance_pct !== null)
          .map(e => ({
            country: e.country,
            year: parseInt((e.yearRange || '').match(/(\d{4})/g)?.slice(-1)[0] || '0'),
            yearRange: e.yearRange,
            value: e.resistance_pct,
            tested: e.N ?? 'N/A',
            source: e.source,
          }));
        phenoSourceLabel = 'Literature';
      }
    } else if (organism === 'styphi' && phenoSource === 'typhi_literature') {
      // Build from bundled Typhi-specific literature data (de-duplicated: keep most-recent year range per ISO3)
      const typhiByIso3 = {};
      typhiCipNSRaw.data.forEach(entry => {
        if (entry.cipNS_pct === null) return;
        const match = (entry.yearRange || '').match(/(\d{4})/g);
        const yearEnd = match ? parseInt(match[match.length - 1]) : 0;
        const existing = typhiByIso3[entry.iso3];
        if (!existing || yearEnd > (existing._yearEnd || 0)) {
          typhiByIso3[entry.iso3] = { ...entry, _yearEnd: yearEnd };
        }
      });
      phenoData = Object.values(typhiByIso3).map(entry => ({
        country: TYPHI_ISO3_TO_AMRNET[entry.iso3] || entry.country,
        year: entry._yearEnd,
        yearRange: entry.yearRange,
        value: entry.cipNS_pct,
        tested: entry.N ?? 'N/A',
        source: entry.source,
      }));
      phenoSourceLabel = 'Literature';
    } else if (glassIndicator.source === 'csv') {
      phenoData = getGLASSPhenotypicByOrganismDrug(glassData, glassIndicator.pathogen, glassIndicator.antibiotic, glassIndicator.specimen);
    } else if (organism === 'ngono') {
      phenoData = Array.isArray(glassData.resistance?.ng_ciprofloxacin) ? glassData.resistance.ng_ciprofloxacin : [];
    } else if (organism === 'saureus') {
      phenoData = glassData.resistance?.mrsa || [];
    } else {
      phenoData = glassData.resistance?.ecoli_3gc || [];
    }

    if (phenoData.length === 0) return empty;

    // Build phenotypic lookup: latest year per country. When the
    // 'Restrict pheno to genomic year range' toggle is on, drop pheno rows
    // whose year falls outside [genoYearStart, genoYearEnd] BEFORE picking
    // the latest — so the chart only ever compares time-aligned data.
    // Note: pheno rows may carry either a single `year` (GLASS) or a
    // `yearRange` (literature). For year-range rows we already extract the
    // end-year into `r.year` upstream, so a single comparison works for
    // both shapes.
    let phenoExcludedCount = 0;
    const phenoByCountry = {};
    phenoData.forEach(r => {
      const ry = Number(r.year);
      if (restrictByYear && Number.isFinite(ry) && (ry < genoYearStart || ry > genoYearEnd)) {
        phenoExcludedCount++;
        return;
      }
      if (!phenoByCountry[r.country] || r.year > phenoByCountry[r.country].year) {
        phenoByCountry[r.country] = r;
      }
    });

    // Get genomic data from AMRnet
    const drugEntries = drugsCountriesData[glassIndicator.drug];
    if (!Array.isArray(drugEntries)) return empty;

    // Build a normalized lookup for fuzzy country matching
    const normalize = s => s?.toLowerCase().replace(/[^a-z]/g, '') || '';
    const phenoNormalized = {};
    Object.keys(phenoByCountry).forEach(c => { phenoNormalized[normalize(c)] = phenoByCountry[c]; });

    // For styphi: build per-country CipNS counts directly from rawOrganismData using
    // cip_pred_pheno column ('CipNS'|'CipR'|'CipS'). getDrugClassData's resistantCount is
    // unreliable for styphi because the susceptible 'None' gene also fires, making
    // resistantCount ≈ total. rawOrganismData gives an exact count per country.
    const styphiCipNSByCountry = {};
    if (organism === 'styphi' && rawOrganismData?.length) {
      rawOrganismData.forEach(item => {
        const key = normalize(item.COUNTRY_ONLY || '');
        if (!styphiCipNSByCountry[key]) styphiCipNSByCountry[key] = { total: 0, cipNS: 0 };
        styphiCipNSByCountry[key].total++;
        if (item.cip_pred_pheno === 'CipNS' || item.cip_pred_pheno === 'CipR') {
          styphiCipNSByCountry[key].cipNS++;
        }
      });
    }

    // Match countries (exact first, then normalized fallback)
    const points = [];
    drugEntries.forEach(entry => {
      const country = entry.name;
      const pheno = phenoByCountry[country] || phenoNormalized[normalize(country)];

      if (pheno && entry.count >= 20) {
        // Guard: pheno.value can be null / undefined / non-numeric during
        // dataset-state transitions (Local ↔ Travel ↔ All / Reset). If we
        // build a scatter point with a NaN x or y, the `<ErrorBar>` reads
        // a NaN range and Recharts' getNiceTickValues crashes the whole
        // tree. Skip the country in that case.
        const phenoValueNum = Number(pheno.value);
        if (!Number.isFinite(phenoValueNum)) return;
        const resistant = organism === 'styphi'
          ? (styphiCipNSByCountry[normalize(country)]?.cipNS ?? 0)
          : entry.resistantCount || 0;
        const total = entry.count;
        const genomicPct = total > 0 ? (resistant / total) * 100 : 0;
        const diff = genomicPct - phenoValueNum;

        // Wilson score 95% CI for genomic estimate
        const ci = wilsonCI(resistant, total);

        // Assess temporal overlap. When restrictByYear is on the build step
        // above has already dropped out-of-range pheno rows, so anything
        // that survives is in-range; with the toggle off we still want to
        // surface mismatched points so they're not invisible.
        const phenoYear = pheno.year;
        const phenoYearDisplay = pheno.yearRange || String(phenoYear);
        const amrnetRange = `${genoYearStart}–${genoYearEnd}`;
        const inRange = Number.isFinite(Number(phenoYear)) && phenoYear >= genoYearStart && phenoYear <= genoYearEnd;
        const yearOverlap = inRange
          ? `Yes (${phenoYearDisplay} within ${amrnetRange})`
          : `No — pheno ${phenoSourceLabel} ${phenoYearDisplay}, genomic ${amrnetRange}`;

        // Concordance: phenotypic value falls within the genomic 95% Wilson CI
        const ciConcordant = phenoValueNum >= ci.lower && phenoValueNum <= ci.upper;
        const category = ciConcordant ? 'concordant' : diff > 0 ? 'overestimate' : 'underestimate';
        const timeMismatch = !inRange;

        const yVal = Number(genomicPct.toFixed(1));
        const ciLower = Number(ci.lower.toFixed(1));
        const ciUpper = Number(ci.upper.toFixed(1));
        const xVal = Number(phenoValueNum.toFixed(1));

        // Final defensive check on every numeric chart field — drop the
        // point if any of them isn't finite (else <ErrorBar> domain NaNs).
        if (!Number.isFinite(xVal) || !Number.isFinite(yVal) ||
            !Number.isFinite(ciLower) || !Number.isFinite(ciUpper)) {
          return;
        }

        points.push({
          country,
          x: xVal,
          y: yVal,
          yError: [yVal - ciLower, ciUpper - yVal],
          genomes: total,
          genomicResistant: resistant,
          genomicCILower: ciLower,
          genomicCIUpper: ciUpper,
          phenoYear,
          phenoYearRange: pheno.yearRange || null,
          phenoTested: pheno.tested || 'N/A',
          phenoSourceLabel,
          diff: Number(diff.toFixed(1)),
          category,
          ciConcordant,
          color: CONCORDANCE_COLORS[category],
          yearOverlap,
          timeMismatch,
        });
      }
    });

    if (points.length < 2) return { ...empty, scatterData: points, stats: { phenoExcludedCount, total: points.length } };

    // Compute statistics — all counts derived from the same `category` field (CI overlap only)
    const concordant = points.filter(p => p.category === 'concordant').length;
    const overestimate = points.filter(p => p.category === 'overestimate').length;
    const underestimate = points.filter(p => p.category === 'underestimate').length;
    const meanAbsDiff = points.reduce((s, p) => s + Math.abs(p.diff), 0) / points.length;
    const sortedDiffs = [...points.map(p => Math.abs(p.diff))].sort((a, b) => a - b);
    const mid = Math.floor(sortedDiffs.length / 2);
    const medianAbsDiff = sortedDiffs.length % 2 === 0
      ? (sortedDiffs[mid - 1] + sortedDiffs[mid]) / 2
      : sortedDiffs[mid];

    // Weighted mean absolute difference (weighted by genomic sample size)
    const totalWeight = points.reduce((s, p) => s + p.genomes, 0);
    const weightedMAD = points.reduce((s, p) => s + Math.abs(p.diff) * p.genomes, 0) / (totalWeight || 1);

    const pears = pearsonR(points);
    const spear = spearmanRho(points);
    const reg = linearRegression(points);

    return {
      scatterData: points,
      rawPhenoData: phenoData,
      regression: reg,
      stats: {
        total: points.length,
        concordant,
        overestimate,
        underestimate,
        concordanceRate: ((concordant / points.length) * 100).toFixed(0),
        meanAbsDiff: meanAbsDiff.toFixed(1),
        medianAbsDiff: medianAbsDiff.toFixed(1),
        weightedMAD: weightedMAD.toFixed(1),
        phenoExcludedCount,
      },
      correlations: {
        pearson: pears,
        spearman: spear,
      },
    };
  }, [
    glassData,
    glassIndicator,
    drugsCountriesData,
    rawOrganismData,
    organism,
    timeInitial,
    timeFinal,
    phenoSource,
    restrictByYear,
    genoYearStart,
    genoYearEnd,
  ]);

  const downloadPhenoCSV = useCallback(() => {
    const escape = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
    // Prepend a header block of '#'-prefixed comment lines that name the
    // primary source and reproduce its attribution. Most CSV readers (Excel,
    // pandas with comment='#', R with comment.char='#') treat these as
    // comments — but they're also human-readable when the file is opened in
    // a plain editor. Per-organism citations live alongside each row in the
    // 'source' and 'doi_or_url' columns.
    const buildAttribution = () => {
      const ts = new Date().toISOString().slice(0, 10);
      if (phenoSource === 'glass') {
        return [
          '# Phenotypic resistance data: WHO Global Antimicrobial Resistance and Use Surveillance System (GLASS).',
          '# Redistributed via AMRnet under WHO data terms — attribution required. Source:',
          '#   https://www.who.int/data/gho/data/themes/topics/global-antimicrobial-resistance-and-use-surveillance-system-glass-database',
          `# Indicator: ${glassIndicator?.label || '—'}`,
          `# Downloaded via amrnet.org on ${ts}.`,
          '#',
        ].join('\n');
      }
      return [
        '# Phenotypic resistance data: aggregated from published surveillance literature',
        '# (ECDC EARS-Net, WHO GASP/GLASS, national reports, GEMS, etc. — see per-row `source` and `doi_or_url`).',
        '# Re-use must cite the original publication(s) listed in each row, not AMRnet.',
        `# Downloaded via amrnet.org on ${ts}.`,
        '#',
      ].join('\n');
    };
    const toCSV = (headers, rows) =>
      buildAttribution() + '\n' + [headers, ...rows].map(r => r.map(escape).join(',')).join('\n');

    let csv, filename;

    if (organism === 'styphi' && phenoSource === 'typhi_literature') {
      // Full literature dataset — include ALL entries (even null cipNS_pct) for transparency
      const headers = ['country', 'iso3', 'region', 'year_range', 'cip_ns_pct', 'cip_r_pct', 'n_tested', 'definition', 'notes', 'source', 'doi_or_url'];
      const rows = typhiCipNSRaw.data.map(r => [
        r.country, r.iso3, r.region, r.yearRange,
        r.cipNS_pct ?? '', r.cipR_pct ?? '', r.N ?? '',
        r.definition ?? '', r.notes ?? '', r.source ?? '', r.doi_or_url ?? '',
      ]);
      csv = toCSV(headers, rows);
      filename = 'amrnet_styphi_ciprofloxacin_phenotypic_literature.csv';
    } else if (phenoSource === 'literature' && LITERATURE_ORGANISMS.has(organism)) {
      // Bundled literature data — use LITERATURE_RAW lookup
      const rawLit = LITERATURE_RAW[organism] || null;
      if (!rawLit) return;
      const headers = ['country', 'iso3', 'region', 'year_range', 'resistance_pct', 'n_tested', 'definition', 'antibiotic', 'source', 'doi_or_url'];
      const rows = rawLit.data.map(r => [
        r.country, r.iso3, r.region, r.yearRange,
        r.resistance_pct ?? '', r.N ?? '', r.definition ?? '', r.antibiotic ?? '', r.source ?? '', r.doi_or_url ?? '',
      ]);
      csv = toCSV(headers, rows);
      filename = `amrnet_${organism}_phenotypic_literature.csv`;
    } else if (rawPhenoData?.length) {
      // GLASS data (styphi+glass or any other organism)
      const hasSpecimen = rawPhenoData[0]?.specimen !== undefined;
      const indicator = glassIndicator?.label || 'WHO GLASS';
      const headers = ['country', 'iso3', 'year', 'percent_resistant', 'n_tested', ...(hasSpecimen ? ['specimen'] : []), 'indicator'];
      const rows = rawPhenoData.map(r => [
        r.country, r.countryCode || '', r.year, r.value ?? '',
        r.tested ?? '', ...(hasSpecimen ? [r.specimen || ''] : []), indicator,
      ]);
      csv = toCSV(headers, rows);
      filename = `amrnet_${organism}_phenotypic_glass.csv`;
    } else {
      return;
    }

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [organism, phenoSource, rawPhenoData, glassIndicator]);

  if (!canGetData) return null;

  return (
    <CardContent className={classes.genomicVsPhenotypicGraph}>
      <Box className={classes.controlsRow}>
        <Typography variant="body2" fontWeight={600}>
          {t('amrInsights.gvp.title')}
        </Typography>
        <Tooltip title={t('amrInsights.gvp.compareTooltip')}>
          <InfoOutlined fontSize="small" sx={{ cursor: 'pointer', color: 'rgba(0,0,0,0.5)' }} />
        </Tooltip>

        {/* Drug + phenotype-source selector — populated from availableDrugs.
            Replaces the previous LITERATURE_ORGANISMS-only toggle and makes
            the drug currently being plotted explicit. */}
        {availableDrugs.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Typography variant="caption" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
              Drug
            </Typography>
            <Select
              value={selectedDrugId ?? availableDrugs[0].id}
              onChange={e => setSelectedDrugId(e.target.value)}
              size="small"
              sx={{ fontSize: '12px', minWidth: 280 }}
            >
              {availableDrugs.map(d => (
                <MenuItem key={d.id} value={d.id}>{d.label}</MenuItem>
              ))}
            </Select>
            <Tooltip title={t('amrInsights.gvp.phenotypicSource.downloadCSV')}>
              <span>
                <IconButton size="small" onClick={downloadPhenoCSV}
                  disabled={phenoSource === 'glass' ? !rawPhenoData?.length : false}>
                  <FileDownload fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        )}

        {loading && phenoSource === 'glass' && <CircularProgress size={16} />}
        {!loading && availableDrugs.length === 0 && (
          <Chip label="No phenotypic data available for this organism" size="small" variant="outlined" color="warning" />
        )}

        {/* Time-alignment guard: default ON. When ON, only phenotypic
            records whose year falls inside the dashboard's genomic year
            range are considered. Prevents accidentally comparing a single
            2021 pheno point against a 2010–2025-pooled geno %R. */}
        <FormControlLabel
          control={<Switch checked={restrictByYear} onChange={(_, val) => setRestrictByYear(val)} size="small" />}
          label={<Typography variant="caption">Restrict pheno to genomic year range</Typography>}
          sx={{ marginLeft: '8px' }}
        />
        <Tooltip title="When on, phenotypic rows outside the dashboard's current year range are dropped before the latest-year row per country is chosen. When off, the chart falls back to the latest available pheno row per country, which may be from a very different period than the genomic data.">
          <InfoOutlined fontSize="small" sx={{ cursor: 'pointer', color: 'rgba(0,0,0,0.5)' }} />
        </Tooltip>
      </Box>

      {/* Period summary — make the comparison's time alignment visible at a
          glance. Without this it's not obvious what year the pheno value
          comes from versus what years the geno %R was computed over. */}
      {availableDrugs.length > 0 && (
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', paddingTop: '2px', paddingBottom: '4px' }}>
          <strong>Genomic period:</strong> {genoYearStart}–{genoYearEnd} (dashboard year filter) ·{' '}
          <strong>Phenotypic:</strong>{' '}
          {restrictByYear
            ? `latest available row per country within ${genoYearStart}–${genoYearEnd}`
            : 'latest available row per country (any year) — time mismatch possible'}
          {stats?.phenoExcludedCount > 0 && (
            <> · <em>{stats.phenoExcludedCount} pheno row(s) dropped as out-of-range</em></>
          )}
        </Typography>
      )}

      {/* Decoli specimen caveat */}
      {organism === 'decoli' && (
        <Alert severity="info" sx={{ marginBottom: '8px', fontSize: '12px', padding: '2px 12px' }}>
          <strong>{t('amrInsights.gvp.specimenNoteLabel')}</strong>{' '}
          <Trans i18nKey="amrInsights.gvp.specimenNote" components={{ em: <em /> }} />
        </Alert>
      )}

      {/* Styphi-specific data source warnings */}
      {organism === 'styphi' && phenoSource === 'glass' && (
        <Alert
          severity="warning"
          icon={<WarningAmber fontSize="small" />}
          sx={{ marginBottom: '8px', fontSize: '12px', padding: '2px 12px' }}
        >
          <strong>{t('amrInsights.gvp.glassCaveatLabel')}</strong>{' '}
          <Trans i18nKey="amrInsights.gvp.glassCaveat" components={{ em: <em />, strong: <strong /> }} />
        </Alert>
      )}
      {organism === 'styphi' && phenoSource === 'typhi_literature' && (
        <Alert severity="info" sx={{ marginBottom: '8px', fontSize: '12px', padding: '2px 12px' }}>
          <strong>{t('amrInsights.gvp.typhiSpecificLabel')}</strong> {t('amrInsights.gvp.typhiSpecific')}
        </Alert>
      )}

      {/* Literature source info for all non-styphi LITERATURE_ORGANISMS */}
      {organism !== 'styphi' && LITERATURE_ORGANISMS.has(organism) && phenoSource === 'literature' && (() => {
        const msgMap = {
          ecoli: { title: 'E. coli ESBL/3GC resistance', detail: 'ECDC EARS-Net 2021 (32 EU countries) + national reports. Bloodstream isolates. EUCAST/CLSI breakpoints.' },
          kpneumo: { title: 'K. pneumoniae carbapenem resistance', detail: 'ECDC EARS-Net 2021 (31 EU countries) + national reports. Bloodstream isolates. EUCAST/CLSI breakpoints.' },
          saureus: { title: 'MRSA prevalence (S. aureus)', detail: 'ECDC EARS-Net 2021 (31 EU countries) + national reports. Bloodstream isolates. mecA/mecC or oxacillin phenotype.' },
          senterica: { title: 'NTS ciprofloxacin resistance (Salmonella)', detail: 'WHO GLASS 2022 + ECDC EARS-Net 2021 + national surveillance. Blood and stool isolates. Note: GLASS data may include S. Typhi in blood specimens.' },
          sentericaints: { title: 'Invasive NTS ciprofloxacin resistance (Salmonella, blood)', detail: 'WHO GLASS 2022 + ECDC EARS-Net 2021 + national surveillance. Shared dataset with non-invasive NTS (senterica); bloodstream-specific breakdown not available in all studies.' },
          decoli: { title: 'E. coli ciprofloxacin resistance (enteric/community)', detail: 'WHO GLASS 2022 + GEMS/MALED studies + national surveillance. Stool and enteric isolates where available; blood/urine used as surrogate for European data. Limited diarrheagenic-specific data.' },
          shige: { title: 'Shigella ciprofloxacin resistance', detail: 'WHO GLASS 2022 + GEMS studies + CHINET + national surveillance. Stool and blood isolates. EUCAST/CLSI breakpoints.' },
          ngono: { title: 'N. gonorrhoeae ciprofloxacin resistance', detail: 'WHO GASP 2017–2018 + ECDC GASP 2021 + CDC GISP 2020. Urogenital isolates. High resistance in Asia-Pacific (>65%), moderate in Europe (25–48%), lower in Americas.' },
          strepneumo: { title: 'S. pneumoniae co-trimoxazole resistance', detail: 'ECDC EARS-Net 2021 (27 EU countries) + WHO GLASS 2022 + national reports. Bloodstream/CSF isolates. High in sub-Saharan Africa (>50%), lower in Europe and East Asia.' },
        };
        const msg = msgMap[organism];
        if (!msg) return null;
        return (
          <Alert severity="info" sx={{ marginBottom: '8px', fontSize: '12px', padding: '2px 12px' }}>
            <strong>{msg.title}:</strong> {msg.detail}
          </Alert>
        );
      })()}

      <Box className={classes.graphWrapper}>
        <Box className={classes.graph}>
          {!glassIndicator ? (
            <Box className={classes.noSelection}>
              <Typography variant="body2" color="textSecondary">
                No GLASS phenotypic data available for this organism.
              </Typography>
            </Box>
          ) : scatterData.length === 0 ? (
            <Box className={classes.noSelection}>
              <Typography variant="body2" color="textSecondary">
                {loading && phenoSource !== 'typhi_literature' && phenoSource !== 'literature'
                  ? 'Loading data...'
                  : 'No matching countries between AMRnet and phenotypic data (N≥20 genomes required).'}
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 20, right: 20, bottom: 40, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" dataKey="x" domain={[0, 100]}>
                  <Label
                    value={
                      phenoSource === 'typhi_literature' ? t('amrInsights.gvp.xAxisTyphiLiterature') :
                      phenoSource === 'literature' && organism === 'ngono' ? t('amrInsights.gvp.xAxisLiteratureNgono') :
                      phenoSource === 'literature' && organism === 'strepneumo' ? t('amrInsights.gvp.xAxisLiteratureStrepneumo') :
                      phenoSource === 'literature' ? t('amrInsights.gvp.xAxisLiteratureDefault') :
                      t('amrInsights.gvp.xAxisGlass')
                    }
                    position="bottom" offset={20} style={{ fontSize: 12 }}
                  />
                </XAxis>
                <YAxis type="number" dataKey="y" domain={[0, 100]}>
                  <Label value={t('amrInsights.gvp.yAxis')} angle={-90} position="insideLeft" offset={10} style={{ fontSize: 12, textAnchor: 'middle' }} />
                </YAxis>
                <ZAxis type="number" dataKey="genomes" range={[40, 300]} name="Genomes" />
                <ChartTooltip content={<CustomTooltip />} />
                {/* Perfect agreement diagonal */}
                <ReferenceLine
                  ifOverflow="extendDomain"
                  segment={[{ x: 0, y: 0 }, { x: 100, y: 100 }]}
                  stroke="#333"
                  strokeDasharray="5 5"
                  strokeWidth={1}
                  label={{ value: t('amrInsights.gvp.perfectAgreement'), position: 'end', fontSize: 10, fill: '#666' }}
                />
                <Scatter data={scatterData}>
                  {scatterData.map((entry, i) => (
                    <Cell
                      key={`cell-${i}`}
                      fill={entry.color}
                      fillOpacity={entry.timeMismatch ? 0.35 : 0.8}
                      stroke={entry.timeMismatch ? '#d32f2f' : 'none'}
                      strokeWidth={entry.timeMismatch ? 1.5 : 0}
                      strokeDasharray={entry.timeMismatch ? '3 2' : undefined}
                    />
                  ))}
                  {/* 95% CI error bars on genomic estimate (Y-axis) */}
                  <ErrorBar
                    dataKey="yError"
                    width={4}
                    strokeWidth={1}
                    stroke="#666"
                    direction="y"
                  />
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          )}
        </Box>

        <Box className={classes.rightSide}>
          {stats && correlations && (
            <>
              {/* Correlation Statistics */}
              <Typography variant="body2" fontWeight={600}>{t('amrInsights.gvp.correlationStatistics')}</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px', padding: '4px 0' }}>
                <Typography variant="caption">
                  <strong>{t('amrInsights.gvp.pearson')}</strong> = {correlations.pearson.r.toFixed(3)} (R² = {correlations.pearson.r2.toFixed(3)}, p {correlations.pearson.p < 0.001 ? '< 0.001' : `≈ ${correlations.pearson.p.toFixed(3)}`})
                </Typography>
                <Typography variant="caption">
                  <strong>Spearman ρ</strong> = {correlations.spearman.rho.toFixed(3)} (p {correlations.spearman.p < 0.001 ? '< 0.001' : `≈ ${correlations.spearman.p.toFixed(3)}`})
                </Typography>
                <Typography variant="caption">
                  <strong>{t('amrInsights.gvp.regression')}</strong>: y = {regression.slope.toFixed(2)}x + {regression.intercept.toFixed(1)}
                </Typography>
              </Box>

              {/* Concordance */}
              <Typography variant="body2" fontWeight={600} sx={{ marginTop: '4px' }}>{t('amrInsights.gvp.concordance')}</Typography>
              <Box className={classes.statsRow}>
                <Box className={classes.concordanceBadge} sx={{ backgroundColor: '#e8f5e9', color: '#1b5e20' }}>
                  <CheckCircleOutline sx={{ fontSize: 14 }} />
                  {stats.concordanceRate}% (95% CI overlap)
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: CONCORDANCE_COLORS.concordant }} />
                  <Typography variant="caption">{t('amrInsights.gvp.concordance')}: {stats.concordant}/{stats.total}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: CONCORDANCE_COLORS.overestimate }} />
                  <Typography variant="caption">{t('amrInsights.gvp.overestimate')}: {stats.overestimate}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: CONCORDANCE_COLORS.underestimate }} />
                  <Typography variant="caption">{t('amrInsights.gvp.underestimate')}: {stats.underestimate}</Typography>
                </Box>
              </Box>

              {/* Error metrics */}
              <Typography variant="body2" fontWeight={600} sx={{ marginTop: '4px' }}>{t('amrInsights.gvp.errorMetrics')}</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <Typography variant="caption">{t('amrInsights.gvp.meanAbsDiff')}: <strong>{stats.meanAbsDiff} {t('amrInsights.gvp.percentagePoints')}</strong></Typography>
                <Typography variant="caption">{t('amrInsights.gvp.medianAbsDiff')}: <strong>{stats.medianAbsDiff} {t('amrInsights.gvp.percentagePoints')}</strong></Typography>
                <Typography variant="caption">{t('amrInsights.gvp.weightedMAD')}: <strong>{stats.weightedMAD} {t('amrInsights.gvp.percentagePoints')}</strong></Typography>
                <Typography variant="caption">{t('amrInsights.gvp.countriesCompared')}: <strong>{stats.total}</strong></Typography>
              </Box>
            </>
          )}

        </Box>
      </Box>

      {/* Floating reference panel — Methodology + Data Sources. The inline
          right-side block keeps the live stats (Correlation, Concordance,
          Error Metrics) so they always show alongside the chart. The dense
          reference text lives here, toggled by the showFilter prop the
          parent AMRInsights wires in. */}
      <PlottingOptionsPanel show={showFilter && !loadingPDF} className={classes.floatingFilter} anchorRef={filterButtonRef}>
          <Card elevation={3}>
            <CardContent>
              <PlottingOptionsHeader onClose={() => setShowFilter(false)} className={classes.titleWrapper} />

              {/* Methodology */}
              <Typography variant="body2" fontWeight={600}>{t('amrInsights.gvp.methodology')}</Typography>
              <Box className={classes.tooltipWrapper} sx={{ marginTop: '4px' }}>
                <Typography variant="caption" sx={{ lineHeight: 1.5 }}>
                  <strong>{t('amrInsights.gvp.matchingLabel')}</strong> {t('amrInsights.gvp.matching')}
                  <br /><br />
                  <strong>{t('amrInsights.gvp.concordanceLabel')}</strong> {t('amrInsights.gvp.concordanceText')}
                  <br /><br />
                  <strong>{t('amrInsights.gvp.errorBarsLabel')}</strong> {t('amrInsights.gvp.errorBars')}
                  <br /><br />
                  <strong>{t('amrInsights.gvp.correlationsLabel')}</strong> {t('amrInsights.gvp.correlations')}
                  <br /><br />
                  <strong>{t('amrInsights.gvp.drugMappingLabel')}</strong>{' '}
                  {phenoSource === 'typhi_literature'
                    ? t('amrInsights.gvp.drugMappingTyphiLiterature')
                    : phenoSource === 'literature' && organism === 'ecoli'
                    ? 'Phenotypic = 3GC non-susceptibility (ESBL proxy) from ECDC EARS-Net 2021 / national reports. Genomic = ESBL gene carriage (AMRnet).'
                    : phenoSource === 'literature' && organism === 'kpneumo'
                    ? 'Phenotypic = Carbapenem non-susceptibility from ECDC EARS-Net 2021 / national reports. Genomic = Carbapenemase gene carriage (AMRnet).'
                    : phenoSource === 'literature' && organism === 'saureus'
                    ? 'Phenotypic = MRSA (mecA/mecC or phenotypic oxacillin resistance) from ECDC EARS-Net 2021 / national reports. Genomic = mecA/mecC gene carriage (AMRnet Methicillin).'
                    : phenoSource === 'literature' && (organism === 'senterica' || organism === 'sentericaints')
                    ? 'Phenotypic = Salmonella ciprofloxacin non-susceptibility from WHO GLASS 2022 / ECDC EARS-Net 2021 / national reports. Genomic = Ciprofloxacin resistance markers (AMRnet).'
                    : phenoSource === 'literature' && organism === 'decoli'
                    ? 'Phenotypic = E. coli ciprofloxacin resistance from community/enteric studies (WHO GLASS, GEMS). Genomic = Ciprofloxacin resistance markers (AMRnet). Note: phenotypic data not specific to diarrheagenic pathotypes.'
                    : phenoSource === 'literature' && organism === 'shige'
                    ? 'Phenotypic = Shigella ciprofloxacin non-susceptibility from WHO GLASS 2022 / GEMS studies / CHINET. Genomic = Ciprofloxacin resistance markers (AMRnet).'
                    : phenoSource === 'literature' && organism === 'ngono'
                    ? 'Phenotypic = N. gonorrhoeae ciprofloxacin resistance (MIC ≥1 mg/L) from WHO GASP / ECDC GASP / CDC GISP. Genomic = Ciprofloxacin resistance markers — primarily GyrA/ParC QRDR mutations (AMRnet).'
                    : phenoSource === 'literature' && organism === 'strepneumo'
                    ? 'Phenotypic = S. pneumoniae co-trimoxazole (SXT) non-susceptibility from ECDC EARS-Net 2021 / WHO GLASS 2022 / national reports. Genomic = Co-Trimoxazole resistance markers — folP/folA variants (AMRnet).'
                    : `${glassIndicator?.label || '—'}. AMRnet = genome-derived (${glassIndicator?.drug || '—'}). Note: genotypic and phenotypic definitions may differ.`
                  }
                  <br /><br />
                  <strong>{t('amrInsights.gvp.caveatsLabel')}</strong>{' '}
                  {phenoSource === 'typhi_literature'
                    ? t('amrInsights.gvp.caveatsTyphiLiterature')
                    : phenoSource === 'literature'
                    ? t('amrInsights.gvp.caveatsLiterature')
                    : t('amrInsights.gvp.caveatsGlass')
                  }
                  <br /><br />
                  <strong>{t('amrInsights.gvp.bubbleSizeLabel')}</strong> ∝ {t('amrInsights.gvp.bubbleSize').replace(/^.*?∝\s*/, '')}
                </Typography>
              </Box>

              {/* Data Sources */}
              <Typography variant="body2" fontWeight={600} sx={{ marginTop: '12px' }}>Data Sources</Typography>
              <Box className={classes.tooltipWrapper} sx={{ marginTop: '4px' }}>
                <Typography variant="caption" sx={{ lineHeight: 1.5 }} component="div">
                  <strong>Phenotypic (X):</strong>{' '}
                  {phenoSource === 'glass' || phenoSource === 'typhi_literature' && organism !== 'styphi' ? (
                    <>
                      <a href={SOURCE_URLS.glass} target="_blank" rel="noopener noreferrer">
                        WHO GLASS
                      </a> — {glassIndicator?.label || 'Global Antimicrobial Resistance and Use Surveillance System'}.
                      {' '}Obtained directly from WHO (GHO OData API) and used under the{' '}
                      <a href={SOURCE_URLS.whoTerms} target="_blank" rel="noopener noreferrer">
                        WHO data terms and conditions
                      </a>.
                    </>
                  ) : (
                    <>
                      Bundled surveillance literature (ECDC EARS-Net 2021 + national reports / WHO GASP / GEMS, depending on
                      the organism). The JSON metadata files in {' '}
                      <code>client/src/assets/</code> carry per-source citations (year ranges, breakpoints, specimen) and a{' '}
                      <code>doi_or_url</code> field for each entry. The download button above exports the full JSON as a CSV.
                      {' '}
                      <a href={SOURCE_URLS.ecdc} target="_blank" rel="noopener noreferrer">
                        See ECDC AMR data hub
                      </a> for primary EARS-Net releases.
                    </>
                  )}
                  <br /><br />
                  <strong>Genomic (Y):</strong>{' '}
                  <a href={SOURCE_URLS.amrnet} target="_blank" rel="noopener noreferrer">AMRnet</a> genome-derived call
                  for <em>{glassIndicator?.drug || '—'}</em> using the standard AMRnet drug-rules engine. Per-country %R is
                  computed across genomes that pass the dashboard's current global filters; the matching N is reported in
                  each tooltip.
                  <br /><br />
                  <strong>Caveat:</strong> phenotypic and genotypic resistance definitions are not always identical (e.g.
                  EUCAST clinical breakpoints vs gene-presence calls), and time periods may not overlap perfectly. See the
                  year-overlap line in each tooltip.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </PlottingOptionsPanel>
    </CardContent>
  );
};
